import * as React from 'react'
import EditIcon from '@mui/icons-material/Edit'
import PreviewIcon from '@mui/icons-material/Preview'
import ArchiveIcon from '@mui/icons-material/Archive'
import RestoreIcon from '@mui/icons-material/Restore'
import {
  GridRowsProp,
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowId,
  GridToolbar,
} from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { ProfileModel } from 'types'
import axios from 'axios'
import { base_url } from 'env'
import { toast } from 'react-hot-toast'
import { Box, Chip, FormControlLabel, Switch } from '@mui/material'
import SearchBar from './SearchBar'
import ExportButton from './ExportButton'

const searchFields = [
  { value: 'firstName', label: 'Prénom' },
  { value: 'lastName', label: 'Nom' },
  { value: 'grade', label: 'Classe' },
  { value: 'createdBy', label: 'Créé par (Login)' },
  { value: 'id', label: 'ID du Profil' },
  { value: 'timeTarget', label: 'Objectif (min)' },
]

export default function ProfileDataGrid({
  profiles,
}: {
  profiles: ProfileModel[]
}) {
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [filteredProfiles, setFilteredProfiles] = React.useState(profiles)
  const [showArchived, setShowArchived] = React.useState(false)
  const navigate = useNavigate()

  const RETENTION_DAYS = 30

  React.useEffect(() => {
    const source = showArchived
      ? filteredProfiles
      : filteredProfiles.filter((p) => p.accountState !== 'archived')
    const newRows = source.map((value) => ({
      ...value,
      id: value.id,
      birthday: dayjs.unix(Number(value.birthday)).toDate(),
      creationDate: dayjs.unix(Number(value.creationDate)).toDate(),
    }))
    setRows(newRows)
  }, [filteredProfiles, showArchived])

  const handlePreviewClick = (id: GridRowId) => () => {
    navigate('/profile/' + id)
  }

  const handleEditClick = (id: GridRowId) => () => {
    navigate('/profile/' + id + '/edit')
  }

  const handleArchiveClick = (id: GridRowId) => () => {
    if (window.confirm(`Êtes-vous sûr de vouloir archiver ce profil ? Il sera supprimé définitivement après la période de rétention.`)) {
      axios
        .delete(`${base_url}/profile/${id}`)
        .then((res) => {
          toast.success('Profil archivé avec succès.')
          const flagDate = Math.floor(Date.now() / 1000)
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === id ? { ...row, accountState: 'archived', flagDate } : row
            )
          )
        })
        .catch((err) => {
          console.error('Failed to archive profile:', err)
        })
    }
  }

  const handleRestoreClick = (id: GridRowId) => () => {
    if (window.confirm(`Restaurer ce profil ?`)) {
      axios
        .post(`${base_url}/profile/${id}/restore`)
        .then(() => {
          toast.success('Profil restauré avec succès.')
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === id ? { ...row, accountState: 'active', flagDate: null } : row
            )
          )
        })
        .catch((err) => {
          console.error('Failed to restore profile:', err)
        })
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Identifiant',
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'firstName',
      headerName: 'Prénom',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'lastName',
      headerName: 'Nom',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'grade',
      headerName: 'Classe',
      width: 100,
    },
    {
      field: 'timeTarget',
      headerName: 'Objectif (min)',
      type: 'number',
      width: 120,
    },
    {
      field: 'accountState',
      headerName: 'État',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value === 'archived' ? 'Archivé' : 'Actif'}
          color={params.value === 'archived' ? 'warning' : 'success'}
          size="small"
        />
      ),
    },
    {
      field: 'flagDate',
      headerName: 'Suppression prévue',
      width: 170,
      renderCell: (params) => {
        if (!params.value) return '—'
        const expectedDate = dayjs.unix(Number(params.value) + RETENTION_DAYS * 86400)
        return expectedDate.format('DD/MM/YYYY')
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        const isArchived = row.accountState === 'archived'
        return [
          <GridActionsCellItem
            icon={<PreviewIcon />}
            label="Aperçu"
            onClick={handlePreviewClick(id)}
          />,
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Modifier"
            onClick={handleEditClick(id)}
            disabled={isArchived}
          />,
          isArchived ? (
            <GridActionsCellItem
              icon={<RestoreIcon />}
              label="Restaurer"
              onClick={handleRestoreClick(id)}
            />
          ) : (
            <GridActionsCellItem
              icon={<ArchiveIcon />}
              label="Archiver"
              onClick={handleArchiveClick(id)}
            />
          ),
        ]
      },
    },
  ]

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          margin: '1rem',
          gap: '1rem',
        }}
      >
        <SearchBar
          onSearch={setFilteredProfiles}
          fields={searchFields}
          originalData={profiles}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
          }
          label="Afficher les archivés"
        />
        <ExportButton
          fields={[
            'id',
            'firstName',
            'lastName',
            'grade',
            'birthday',
            'active',
            'creationDate',
            'testLocked',
            'createdBy',
            'surveyId',
            'suggestedActivity',
            'title',
            'timeTarget',
            'accountState',
          ]}
          data={profiles}
          exportFileName="profiles_export"
        />
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        slots={{ toolbar: GridToolbar }}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        autosizeOnMount={true}
      />
    </Box>
  )
}
